/* eslint-disable no-nested-ternary */
import { ReceiptLong } from '@mui/icons-material';
import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { PosShift } from '../../apiTypes';
import DataTable, { Column } from '../../components/DataTable';
import StyledTextField from '../../components/styled/StyledTextField';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { useCollection } from '../../utils/api';
import useMedia from '../../utils/useMedia';
import ShiftPrintModal from './ShiftPrintModal';

type Row = DbDocument<PosShift>;

const PosShiftArchive = () => {
  const [date, setDate] = useState<Date | null>(new Date());
  const { lang } = useLocale();
  const { cafeSlug, cafe, getCurrency } = useAdmin();
  const { workersMap, worker } = useCafe();
  const { isMobile } = useMedia();

  const [shift, setShit] = useState<DbDocument<PosShift> | null>(null);

  const dateRange = useMemo(() => {
    const endDate = new Date(date ? date.getTime() : 0);
    endDate.setHours(0);
    endDate.setMinutes(0);
    endDate.setSeconds(0);
    const startDate = new Date(date ? date.getTime() : 0);
    startDate.setHours(23);
    startDate.setMinutes(59);
    startDate.setSeconds(59);

    return {
      end: endDate.getTime(),
      start: startDate.getTime(),
    };
  }, [date]);

  const { data: shifts } = useCollection<PosShift>(`cafes/${cafeSlug}/posShifts`, {
    where: ['created', '>', dateRange.end],
    where2: ['created', '<', dateRange.start],
    orderBy: ['created', 'desc'],
  });

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'created',
        title: t.created[lang],
        align: 'left',
        render: (r: Row) => (
          <Stack direction="row" alignItems="center" spacing={{ xs: 1 }}>
            <Avatar src={workersMap[r.createdBy.workerId]?.photoUrl} />
            <div>
              <Typography variant="body2">{workersMap[r.createdBy.workerId]?.name || r.createdBy.name}</Typography>
              <Typography variant="body2">{format(new Date(r.created), 'dd.MM HH:mm')}</Typography>
            </div>
          </Stack>
        ),
      },
      {
        name: 'status',
        title: t.status[lang],
        align: 'left',
        render: (r: Row) => <Typography>{r.closed ? t.shift_closed[lang] : t.created[lang]}</Typography>,
      },
      {
        name: 'details',
        title: '',
        align: 'right',
        render: (r: Row) =>
          r.startWalletState.length ? (
            <IconButton onClick={() => setShit(r)} disabled={!worker?.credentials.print_bill}>
              <ReceiptLong />
            </IconButton>
          ) : null,
      },
    ],
    [lang, worker?.credentials.print_bill, workersMap]
  );

  return (
    <Box sx={{ maxWidth: 800, m: '0 auto', pb: 2 }}>
      <DataTable
        columns={columns}
        rows={shifts || []}
        perPage={10}
        dataTestIdPrefix="pos-shift"
        customHeader={
          <DatePicker
            label={t.date[lang]}
            value={date}
            onChange={(newValue) => setDate(newValue)}
            renderInput={(params) => <StyledTextField {...params} size="small" />}
            disableFuture
          />
        }
      />

      {shift && <ShiftPrintModal onClose={() => setShit(null)} shift={shift} />}
    </Box>
  );
};

export default PosShiftArchive;
